import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryV2 } from './api';

const BASE_URL = '/consultation-service/therapy-diary/psychologist';

/** Инструменты, которые принимает дневник. */
export type TherapyDiaryToolType =
  | 'emotions'
  | 'mac_cards'
  | 'wheel'
  | 'color_code'
  | 'clouds'
  | 'sand_therapy'
  | 'dpdg';

/** Текст задания. В свагере поле описано как object, но сервер валидирует его как строку. */
export type ITherapyDiaryAssignment = string;

/** Привязка к конкретному срабатыванию инструмента в сессии. Для dpdg не требуется. */
export interface ITherapyDiaryItemSource {
  date_time: string;
  occurrence_index: number;
}

export interface ITherapyDiaryItemInput {
  tool_type: TherapyDiaryToolType;
  // Обязательно для всех инструментов, кроме dpdg — там результат не формируется, source не нужен.
  source?: ITherapyDiaryItemSource;
  assignment?: ITherapyDiaryAssignment | null;
}

export interface ITherapyDiaryItemResponse extends ITherapyDiaryItemInput {
  id: number;
  sort_order?: number;
  result?: unknown;
}

export interface ITherapyDiaryOrigin {
  type: 'manual' | 'ai_homework';
  is_modified?: boolean;
}

export interface ITherapyDiaryEntry {
  id: number;
  consultation_id: number;
  client_entity_id: number;
  psychologist_id: number;
  status: 'draft' | 'sent';
  general_assignment: ITherapyDiaryAssignment | null;
  items: ITherapyDiaryItemResponse[];
  origin: ITherapyDiaryOrigin;
  send_version: number;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ICreateTherapyDiaryEntryDto {
  consultation_id: number;
  general_assignment?: ITherapyDiaryAssignment | null;
  items?: ITherapyDiaryItemInput[];
}

export interface IUpdateTherapyDiaryEntryDto {
  general_assignment?: ITherapyDiaryAssignment | null;
  items: ITherapyDiaryItemInput[];
}

export interface ITherapyDiaryEntriesResponse {
  items: ITherapyDiaryEntry[];
  page: number;
  limit: number;
  total: number;
}

export const therapyDiaryApi = createApi({
  reducerPath: 'therapyDiaryApi',
  baseQuery: baseQueryV2,
  tagTypes: ['TherapyDiaryEntry'],
  endpoints: (build) => ({
    getTherapyDiaryEntries: build.query<ITherapyDiaryEntriesResponse, { consultation_id: number }>({
      query: ({ consultation_id }) => ({
        url: `${BASE_URL}/entries`,
        method: 'GET',
        params: { consultation_id },
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'TherapyDiaryEntry', id: arg.consultation_id }],
    }),
    createTherapyDiaryEntry: build.mutation<ITherapyDiaryEntry, ICreateTherapyDiaryEntryDto>({
      query: (body) => ({
        url: `${BASE_URL}/entries`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'TherapyDiaryEntry', id: arg.consultation_id }],
    }),
    // consultationId в аргументе нужен только для тега: список запрашивается по консультации,
    // инвалидировать по id записи бесполезно.
    updateTherapyDiaryEntry: build.mutation<
      ITherapyDiaryEntry,
      { id: number; consultationId: number; body: IUpdateTherapyDiaryEntryDto }
    >({
      query: ({ id, body }) => ({
        url: `${BASE_URL}/entries/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'TherapyDiaryEntry', id: arg.consultationId }],
    }),
    sendTherapyDiaryEntry: build.mutation<ITherapyDiaryEntry, { id: number; consultationId: number }>({
      // Без ключа body: с body: {} fetchBaseQuery отправит «{}» и Content-Type, а ручка ждёт пустое тело.
      query: ({ id }) => ({
        url: `${BASE_URL}/entries/${id}/send`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'TherapyDiaryEntry', id: arg.consultationId }],
    }),
    // Возвращает запись в статус draft: её снова можно править и отправить заново.
    // 409 — отозвать уже нельзя (например, клиент успел выполнить задание).
    recallTherapyDiaryEntry: build.mutation<ITherapyDiaryEntry, { id: number; consultationId: number }>({
      query: ({ id }) => ({
        url: `${BASE_URL}/entries/${id}/recall`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'TherapyDiaryEntry', id: arg.consultationId }],
    }),
  }),
});

export const {
  useGetTherapyDiaryEntriesQuery,
  useCreateTherapyDiaryEntryMutation,
  useUpdateTherapyDiaryEntryMutation,
  useSendTherapyDiaryEntryMutation,
  useRecallTherapyDiaryEntryMutation,
} = therapyDiaryApi;
